import { useLayoutEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCart } from '../store/cart';
import { api, ApiError } from '../lib/api';
import { amount, percentFromBps, quantity, unitLabel } from '../lib/format';
import { monogram, toneOf } from '../lib/till';
import type { Paged, PosProduct } from '../lib/types';
import { Icon } from './icons';
import { Button, cx, ErrorState, Kbd, Skeleton } from './ui';
import { tr, useT } from '../i18n';

/**
 * Quick keys.
 *
 * Most items in a supermarket are scanned, not tapped, so this grid exists for what scanning
 * cannot cover: loose produce with no barcode, a customer naming a product, and the handful
 * of things every queue buys. It shows the catalogue as big tap targets, narrowed by category
 * chips or by a pinned favourites list (kept on this terminal). Finding an item by name is the
 * scan bar's and the search palette's job (`onFind`), so there is one place to type, not two.
 *
 * Every tile carries its aisle's colour and a two-letter mark, the Marathi / Hindi shelf name,
 * and one state: in the bill, running low, out, or how much is left.
 */

interface Props {
  onPick: (product: PosProduct) => void;
  /** Opens the search palette. */
  onFind?: () => void;
  findKey?: string;
  disabled?: boolean;
}

interface Category {
  id: string;
  name: string;
  productCount: number;
}

const FAV_KEY = 'sp.till.favourites';
const PAGE_SIZE = 40;

function readFavourites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string').slice(0, 40) : [];
  } catch {
    return [];
  }
}

function messageOf(error: unknown): string {
  return error instanceof ApiError ? error.message : tr('grid.loadFailed');
}

export function ProductGrid({ onPick, onFind, findKey = 'F2', disabled = false }: Props) {
  const t = useT();
  const [categoryId, setCategoryId] = useState<string | 'favourites' | null>(null);
  const [page, setPage] = useState(0);
  const [favourites, setFavourites] = useState<string[]>(readFavourites);
  /** True once the category row is scrolled to its end, which clears the edge fade. */
  const [chipsAtEnd, setChipsAtEnd] = useState(false);
  const chipRow = useRef<HTMLDivElement>(null);
  const cartLines = useCart((state) => state.lines);

  const categories = useQuery({
    queryKey: ['till', 'categories'],
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) =>
      api.get<{ categories: Category[] }>('/api/catalog/categories', undefined, signal).then((result) => result.categories),
  });

  /**
   * The edge fade is only right while the row can actually scroll. A row that fits never fires a
   * scroll event, so the answer has to be measured after layout — and again whenever the chips
   * arrive or the row changes width — or the last category stays dimmed with nothing to reveal.
   */
  useLayoutEffect(() => {
    const row = chipRow.current;
    if (!row) return;
    const measure = () => setChipsAtEnd(row.scrollLeft + row.clientWidth >= row.scrollWidth - 2);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [categories.data, favourites.length]);

  const listing = useQuery({
    queryKey: ['till', 'products', '', categoryId === 'favourites' ? null : categoryId, page],
    enabled: categoryId !== 'favourites',
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      api.get<Paged<PosProduct>>(
        '/api/catalog/products',
        {
          categoryId: categoryId ?? undefined,
          inStock: true,
          page,
          pageSize: PAGE_SIZE,
        },
        signal,
      ),
  });

  const favouriteProducts = useQuery({
    queryKey: ['till', 'favourites', favourites],
    enabled: categoryId === 'favourites' && favourites.length > 0,
    queryFn: async ({ signal }) => {
      const found = await Promise.all(
        favourites.map((id) =>
          api
            .get<{ product: PosProduct }>(`/api/catalog/products/${id}`, undefined, signal)
            .then((result) => result.product)
            .catch(() => null),
        ),
      );
      return found.filter((product): product is PosProduct => product !== null && product.isActive);
    },
  });

  function toggleFavourite(productId: string) {
    setFavourites((current) => {
      const next = current.includes(productId) ? current.filter((id) => id !== productId) : [...current, productId];
      try {
        localStorage.setItem(FAV_KEY, JSON.stringify(next));
      } catch {
        // storage blocked: the pin lasts until the page reloads
      }
      return next;
    });
  }

  const showingFavourites = categoryId === 'favourites';
  const active = showingFavourites ? favouriteProducts : listing;
  const items = showingFavourites ? (favouriteProducts.data ?? []) : (listing.data?.items ?? []);
  const loading = active.isLoading || (showingFavourites && favourites.length > 0 && favouriteProducts.isLoading);
  const totalPages = !showingFavourites ? (listing.data?.totalPages ?? 1) : 1;

  const inCartCount = (productId: string) =>
    cartLines.filter((line) => line.productId === productId).reduce((sum, line) => sum + line.qtyMilli, 0);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 pb-3">
        {onFind ? (
          <button
            type="button"
            className="btn btn-touch shrink-0 rounded-full px-3.5"
            disabled={disabled}
            onClick={onFind}
            data-testid="till-find"
            title={t('grid.findTitle')}
          >
            <Icon name="search" size={17} />
            <span className="hidden sm:inline">{t('grid.find')}</span>
            <Kbd className="hidden lg:inline-flex">{findKey}</Kbd>
          </button>
        ) : null}

        <div
          ref={chipRow}
          className="scroll-hide chip-fade -mx-1 flex min-w-0 flex-1 gap-2 overflow-x-auto px-1 py-0.5"
          role="group"
          aria-label={t('grid.categories')}
          data-at-end={chipsAtEnd ? 'true' : 'false'}
          onScroll={(event) => {
            const row = event.currentTarget;
            setChipsAtEnd(row.scrollLeft + row.clientWidth >= row.scrollWidth - 2);
          }}
        >
          <button type="button" className="chip" aria-pressed={categoryId === null} onClick={() => { setCategoryId(null); setPage(0); }}>
            {t('grid.all')}
          </button>
          {favourites.length > 0 ? (
            <button type="button" className="chip gap-1.5" aria-pressed={categoryId === 'favourites'} onClick={() => setCategoryId('favourites')}>
              <Icon name="star" size={15} fill="currentColor" />
              {t('grid.favourites', { count: favourites.length })}
            </button>
          ) : null}
          {(categories.data ?? [])
            .filter((category) => category.productCount > 0)
            .map((category) => (
              <button
                key={category.id}
                type="button"
                className={cx('chip gap-2', `tone-${toneOf(category.name)}`)}
                aria-pressed={categoryId === category.id}
                onClick={() => {
                  setCategoryId(category.id);
                  setPage(0);
                }}
              >
                <span aria-hidden className="tone-dot" />
                {category.name}
              </button>
            ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-1" aria-busy={loading}>
        {active.isError ? (
          <ErrorState title={t('grid.unavailable')} message={messageOf(active.error)} onRetry={() => void active.refetch()} />
        ) : null}

        {loading ? (
          <div className="quick-grid" role="status" aria-label={t('grid.loading')}>
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="h-[6.75rem]" />
            ))}
          </div>
        ) : null}

        {!loading && !active.isError && items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-2 py-10 text-center text-sm text-slate-500">
            <span
              aria-hidden
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{ background: 'var(--ui-surface-2)', color: 'var(--ui-faint)' }}
            >
              <Icon name={showingFavourites ? 'star' : 'search'} size={22} />
            </span>
            <p>{showingFavourites ? t('grid.noFavourites') : t('grid.empty')}</p>
          </div>
        ) : null}

        {!loading && items.length > 0 ? (
          <ul className="quick-grid" aria-label={t('grid.quickKeys')}>
            {items.map((product) => {
              const inCart = inCartCount(product.id);
              const outOfStock = product.stockMilli <= 0;
              const low = !outOfStock && product.minimumStockMilli > 0 && product.stockMilli <= product.minimumStockMilli;
              const pinned = favourites.includes(product.id);
              const unit = unitLabel(product.unit);
              return (
                <li key={product.id} className="relative">
                  <button
                    type="button"
                    disabled={disabled || outOfStock}
                    onClick={() => onPick(product)}
                    className="quick-key h-full w-full"
                    data-testid="quick-key"
                    data-incart={inCart > 0 ? 'true' : undefined}
                    title={t('grid.gst', { percent: percentFromBps(product.gstBps) }) + (product.allowDecimalQty ? ` · ${t('grid.opensWeighPad')}` : '')}
                    aria-label={t('grid.addItem', { name: product.name, price: amount(product.sellingPricePaise), unit })}
                  >
                    <span className="flex items-start gap-2.5 pr-7">
                      <span aria-hidden className={cx('mono-badge h-9 w-9 text-xs max-lg:hidden', `tone-${toneOf(product.categoryName)}`)}>
                        {monogram(product.name)}
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm leading-snug font-semibold text-slate-900 max-lg:line-clamp-3">{product.name}</span>
                        {product.localName ? (
                          <span className="block truncate text-[13px] leading-snug text-slate-500">{product.localName}</span>
                        ) : null}
                      </span>
                    </span>
                    <span className="flex items-end justify-between gap-2">
                      <span className="tabular flex items-baseline gap-0.5 text-base leading-none font-bold whitespace-nowrap text-slate-900">
                        ₹{amount(product.sellingPricePaise)}
                        <span className="text-xs font-medium text-slate-500">/{unit}</span>
                        {product.allowDecimalQty ? (
                          <Icon name="scale" size={14} className="ml-1 self-center text-slate-400" aria-hidden />
                        ) : null}
                      </span>
                      {inCart > 0 ? (
                        <span className="badge badge-primary">{t('grid.inBill', { qty: quantity(inCart) })}</span>
                      ) : outOfStock ? (
                        <span className="badge badge-danger">{t('grid.outOfStock')}</span>
                      ) : low ? (
                        <span className="badge badge-warn">{t('grid.low', { qty: quantity(product.stockMilli) })}</span>
                      ) : (
                        <span className="text-xs font-medium whitespace-nowrap text-slate-500">
                          {product.allowDecimalQty ? `${quantity(product.stockMilli)} ${unit}` : t('grid.left', { qty: quantity(product.stockMilli) })}
                        </span>
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="absolute top-1 right-1 flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-amber-600"
                    style={pinned ? { color: 'var(--ui-warn-solid)' } : undefined}
                    aria-pressed={pinned}
                    aria-label={pinned ? t('grid.unpin', { name: product.name }) : t('grid.pin', { name: product.name })}
                    onClick={() => toggleFavourite(product.id)}
                  >
                    <Icon name="star" size={17} fill={pinned ? 'currentColor' : 'none'} />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {totalPages > 1 ? (
          <div className="mt-3 flex items-center justify-between gap-2 text-xs">
            <Button size="sm" disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))}>
              {t('common.previous')}
            </Button>
            <span className="text-slate-500">
              {t('common.page', { page: page + 1, pages: totalPages })}
              {listing.data ? ` · ${t('grid.count', { count: listing.data.total })}` : ''}
            </span>
            <Button size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((current) => current + 1)}>
              {t('common.next')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
